import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Grid,
  Typography,
  Card,
  Box,
  CardHeader,
  CardContent,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import Button from "../../atoms/Button";
import { observer } from "mobx-react";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import useStore from "../../helpers/useStore";
import { useMasterTables } from "../../stores/MasterTables";
import { FullScreenDialog } from "../../atoms/FullScreenDialog";
import { TopLevelPaper } from "../../themes/StyledComponents";
import Controls from "../../atoms/Controls";
import { RoleDto, UserDto } from "../../api-client";
import { useUsersByLocation } from "../../stores/Calendar/calendar.queries";
import notificationStore from "../../stores/Notification/notificationStore";
import { useLocationId } from "../../stores/User/User.hooks";
interface UserSelectorProps {
  open: boolean;
  onClose?: () => void;
  locationId: string;
  onConfirm: any;
}

const UserSelector: React.FC<UserSelectorProps> = observer(
  ({ open, onClose = () => {}, onConfirm }) => {
    const CalendarStore = useStore(CalendarContext);
    const locationId = useLocationId();
    const { 
      columnUserIds, 
      setColumnUserIds: setSelectedUsersGlobal,
      isRoleMode,
      setIsRoleMode,
      roleForUserList,
      setRoleForUserList
    } = CalendarStore; // selected user ids for calendar columns
    const { data: usersData = [], isLoading: isLoadingUsers } = useUsersByLocation(
      locationId,
      undefined, // no filtering, getting all users
      true,
      !!locationId
    );
    // console.debug("[UserSelector] Render", { columnUserIds, usersData });

    const { data: masterTablesData } = useMasterTables();
    const roles = (masterTablesData || [])
      .filter((obj: any) => "role" in obj)
      .map((obj: any) => obj.role)[0];

    // Local state
    const [mode, setMode] = useState<'user' | 'role'>(isRoleMode ? 'role' : 'user');
    const [selectedRole, setSelectedRole] = useState<RoleDto | undefined>(undefined);
    const [localSelectedUsers, setLocalSelectedUsers] = useState<UserDto[]>([]);
    const [showDeactivatedUsers, setShowDeactivatedUsers] = useState<boolean>(true);

    // Keep local mode in sync with store
    useEffect(() => {
      setMode(isRoleMode ? 'role' : 'user');
    }, [isRoleMode]);

    const handleModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'user' | 'role' | null) => {
      if (newMode !== null) {
        setMode(newMode);
        setIsRoleMode(newMode === 'role');
        if (newMode === 'role') {
          // Clear columnUserIds when switching to role mode
          setSelectedUsersGlobal([]);
        }
      }
    };

    const handleShowDeactivatedUsersChange = (_event: React.MouseEvent<HTMLElement>, newValue: boolean | null) => {
      if (newValue !== null) {
        setShowDeactivatedUsers(newValue);
      }
    };

    // Derived Values
    const usersFilteredByRole = selectedRole
      ? usersData.filter((user) => {
        if (mode === "user") {
            // In user mode, check both role and deletion status
            return user.roles?.some((role: RoleDto) => role.id === selectedRole?.id) && 
                  (showDeactivatedUsers || user.deletedAt === null); // Show all if showDeactivatedUsers is true, otherwise only show non-deleted
          } else {
            // In role mode, only check role
            return user.roles?.some((role: RoleDto) => role.id === selectedRole?.id);
          }
        })
      : usersData;
    const availableUsers = usersFilteredByRole.filter(
      (user) => !localSelectedUsers.some((u) => u.id === user.id)
    );

    // Set initial role from store when opening modal
    useEffect(() => {
      if (!open || !roles) return;
      const storeRole = roles.find((role: RoleDto) => role.id === roleForUserList);
      if (storeRole) {
        handleRoleChange(storeRole);
      }
    }, [open, roles]);

    // initialize the selectedUsers once all the initial data are loaded
    useEffect(() => {
      if (!open) return;
      if (!usersData || usersData.length === 0) return;

      // if the user data are available, then we can procced to set the initial selected users
      const initialSelectedUsers: UserDto[] = [];
      if (columnUserIds.length > 0) { // if the store contains values, use them
        usersData.forEach((user) => {
          if (columnUserIds.includes(user.id!)) {
            initialSelectedUsers.push(user);
          }
        });
      } else {
        usersData.forEach((user) => {  // if nothing else is provided, we can choose therapists by default
          if (user.roles?.some((role) => role.id === "therapist")) {
            initialSelectedUsers.push(user);
          }
        });
      }
      const displaySelectedUsers =
        localSelectedUsers.length > 0 ? localSelectedUsers : initialSelectedUsers;
      setLocalSelectedUsers(displaySelectedUsers);
    }, [open, usersData]);

    const addUser = (user: UserDto) => {
      if (!localSelectedUsers.some((u) => u.id === user.id)) {
        setSelectedUsersLocal([...localSelectedUsers, user]);
      }
    };

    const removeUser = (user: UserDto) => {
      setSelectedUsersLocal(localSelectedUsers.filter((u) => u.id !== user.id));
    };

    const clearAll = () => {
      // setAvailableUsers([...availableUsers, ...localSelectedUsers]);
      setSelectedUsersLocal([]);
    };

    const addAllUsers = () => {
      if(!selectedRole) notificationStore.showMessage("Bitte wählen Sie eine Rolle aus, um alle Benutzer hinzuzufügen.","warning");
      const usersToMove = availableUsers.filter((user) =>
        user.roles?.some((role: RoleDto) => role.id === selectedRole?.id)
      );

      const newSelected = [
        ...localSelectedUsers,
        ...usersToMove.filter((user) => !localSelectedUsers.some((u) => u.id === user.id)),
      ];
      setSelectedUsersLocal(newSelected);

      // const newAvailable = availableUsers.filter(
      //   (user) => !usersToMove.some((u) => u.id === user.id)
      // );
      // setAvailableUsers(newAvailable);
    };

    const handleRoleChange = (role: RoleDto) => {
      setSelectedRole(role);
      setRoleForUserList(role.id); // Save selected role in store
      
      if (mode === 'role') {
        // Find all users with this role and put them in the right panel
        const usersWithThisRole = usersData.filter((user) =>
          user.roles?.some((userRole: RoleDto) => userRole.id === role.id)
        );
        setLocalSelectedUsers(usersWithThisRole);
        // Clear columnUserIds in role mode since we use roleForUserList instead
        setSelectedUsersGlobal([]);
      }
    };

    const handleApplyUsers = () => {
      // console.log("handleApplyUsers called");
      setSelectedUsersGlobal(localSelectedUsers.map((u) => u.id) as string[]);

      onConfirm?.();
      onClose();
    };

    const setSelectedUsersLocal = (users: any[]) => {
      setLocalSelectedUsers(users);
    };

    return (
      <FullScreenDialog
        open={open}
        onClose={onClose}
        titleComp="Auswahl der Benutzer"
        actions={
          <>
            <Button
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                // console.log("Cancel button clicked");
                onClose();
              }}
              variant="text"
            >
              Abbrechen
            </Button>
            <Button
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                handleApplyUsers();
              }}
              variant="contained"
            >
              Speichern
            </Button>
          </>
        }
      >
        {isLoadingUsers && <CircularProgress color="inherit" />}
        {!isLoadingUsers && (
          <Grid container spacing={3}>
            {" "}
            {/* Users Pool Section */}
            <Grid item xs={6}>
              <TopLevelPaper
                sx={{
                  height: "100%",
                  overflowY: "auto",
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                  <Card>
                    <CardHeader title="Benutzer Pool" />
                    <CardContent sx={{ overflowY: "auto" }}>
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <ToggleButtonGroup
                          value={mode}
                          exclusive
                          onChange={handleModeChange}
                          aria-label="selection mode"
                        >
                          <ToggleButton value="role">Role</ToggleButton>
                          <ToggleButton value="user">User</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      { mode === "user" && <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <ToggleButtonGroup
                          value={showDeactivatedUsers}
                          exclusive
                          onChange={handleShowDeactivatedUsersChange}
                          aria-label="deactivated selection mode"
                        >
                          <ToggleButton value={true}>Bei deaktiviertem</ToggleButton>
                          <ToggleButton value={false}>Ohne</ToggleButton>
                        </ToggleButtonGroup>
                      </Box>}
                      <Controls.Select
                        name="role"
                        label="Role"
                        id={1}
                        value={selectedRole?.name}
                        onChange={(event: any) => handleRoleChange(event.target.id)}
                        options={roles?.map((role: any) => ({
                          label: role.name,
                          value: role.name,
                          id: role,
                        }))}
                        placeholder="Rolle auswählen"
                        showOptionLabel
                        style={{ width: "50%", marginBottom: 16 }}
                      />

                      {mode === 'user' && <Grid container justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
                        <Grid item>
                          <Button variant="contained" size="small" onClick={addAllUsers}>
                            Alle hinzufügen
                          </Button>
                        </Grid>
                      </Grid>}

                      <Grid container spacing={2}>
                        {availableUsers.length > 0 ? (
                          availableUsers.map((user) => (
                            <Grid item key={user.id} xs={12} sm={6} md={4}>
                              <Button
                                variant="contained"
                                size="large"
                                onClick={() => addUser(user)}
                                disabled={mode === 'role'}
                                fullWidth
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  padding: "12px",
                                  borderRadius: "8px",
                                  boxShadow: 2,
                                  "&:hover": {
                                    boxShadow: 5,
                                  },
                                }}
                              >
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {user.firstName} {user.lastName} ({user.nickName})
                                </Typography>
                                <AddIcon fontSize="small" />
                              </Button>
                            </Grid>
                          ))
                        ) : (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                              No users available
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </TopLevelPaper>
            </Grid>
            {/* Selected Users Section */}
            <Grid item xs={6}>
              <TopLevelPaper
                sx={{
                  height: "100%",
                  overflowY: "auto",
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
                  <Card>
                    <CardHeader title="Ausgewählte Benutzer" />
                    <CardContent sx={{ overflowY: "auto" }}>
                      {localSelectedUsers.length > 0 ? (
                        <>
                          { mode === 'user' && <Grid
                            container
                            justifyContent="flex-end"
                            alignItems="center"
                            sx={{ mb: 2 }}
                          >
                            <Grid item>
                              <Button variant="contained" size="small" onClick={clearAll}>
                                Alle entfernen
                              </Button>
                            </Grid>
                          </Grid>}

                          <Grid container spacing={2}>
                            {localSelectedUsers.map((user) => (
                              <Grid item key={user.id} xs={12} sm={6} md={4}>
                                <Button
                                  variant="contained"
                                  size="large"
                                  onClick={() => removeUser(user)}
                                  disabled={mode === 'role'}
                                  fullWidth
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    boxShadow: 2,
                                    "&:hover": {
                                      boxShadow: 5,
                                    },
                                  }}
                                >
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {user.firstName} {user.lastName} ({user.nickName})
                                  </Typography>
                                  <RemoveIcon fontSize="small" />
                                </Button>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No users selected
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </TopLevelPaper>
            </Grid>
          </Grid>
        )}
      </FullScreenDialog>
    );
  }
);

export default UserSelector;
